import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { getAdminDonors, createAdminDonor, updateAdminDonor, deleteAdminDonor } from '@/services/admin/donors.admin.api';
import type { FrontendDonor, DonorTier } from '@/services/transform';
import ImageUploader from '@/components/base/ImageUploader';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';

// 使用 FrontendDonor 作为 Donor 类型
type Donor = FrontendDonor;

const TIER_LABEL: Record<DonorTier, string> = { diamond: '钻石', gold: '金牌', silver: '银牌', honorary: '荣誉', strategic: '战略' };
const TIER_COLOR: Record<DonorTier, string> = {
  diamond: 'bg-cyan-100 text-cyan-700',
  gold: 'bg-amber-100 text-amber-700',
  silver: 'bg-gray-100 text-gray-600',
  honorary: 'bg-violet-100 text-violet-700',
  strategic: 'bg-teal-100 text-teal-700',
};

const BLANK: Partial<Donor> = {
  name: '', nameEn: '', type: '赞助商', typeEn: 'Sponsor', tier: 'gold',
  abbr: '', color: '#F59E0B', year: new Date().getFullYear(), descZh: '', descEn: '', logo: '',
};

export default function DonorsPage() {
  const { can } = useRBAC();
  const canView = can('donors:view');
  const canWrite = can('donors:write');
  const canDelete = can('donors:delete');
  const [donors, setDonors] = useState<Donor[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Donor | null>(null);
  const [form, setForm] = useState<Partial<Donor>>(BLANK);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  // 加载数据
  useEffect(() => {
    loadDonors();
  }, []);

  const loadDonors = async () => {
    setLoading(true);
    try {
      const data = await getAdminDonors();
      setDonors(data);
    } catch (error) {
      console.error('加载捐赠人失败:', error);
      showToast('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return donors.filter((d) => {
      if (filterTier !== 'all' && d.tier !== filterTier) return false;
      if (search && !d.name.includes(search) && !d.nameEn.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [donors, search, filterTier]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...BLANK, year: new Date().getFullYear() });
    setShowModal(true);
  };

  const openEdit = (d: Donor) => {
    setEditing(d);
    setForm({ ...d });
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    
    // 创建要保存的数据
    const donorData: Partial<Donor> = {
      name: form.name || '',
      nameEn: form.nameEn || '',
      type: form.type || '赞助商',
      typeEn: form.typeEn || 'Sponsor',
      tier: (form.tier || 'gold') as DonorTier,
      abbr: form.abbr || (form.name || '').slice(0, 3).toUpperCase(),
      color: form.color || '#F59E0B',
      year: form.year || new Date().getFullYear(),
      descZh: form.descZh || '',
      descEn: form.descEn || '',
      logo: form.logo || '',
    };

    try {
      if (editing && editing.id) {
        // 更新现有捐赠人
        const result = await updateAdminDonor(editing.id, donorData);
        if (result) {
          setDonors(donors.map((d) => d.id === editing.id ? result : d));
          showToast('捐赠人信息已更新');
        } else {
          showToast('更新失败');
        }
      } else {
        // 创建新捐赠人
        const result = await createAdminDonor(donorData);
        if (result) {
          setDonors([...donors, result]);
          showToast('捐赠人添加成功');
        } else {
          showToast('添加失败');
        }
      }
      setShowModal(false);
    } catch (error) {
      console.error('保存捐赠人失败:', error);
      showToast('保存失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const success = await deleteAdminDonor(id);
      if (success) {
        setDonors(donors.filter((d) => d.id !== id));
        setDeleteConfirm(null);
        showToast('已删除');
      } else {
        showToast('删除失败');
      }
    } catch (error) {
      console.error('删除捐赠人失败:', error);
      showToast('删除失败');
    }
  };

  const f = (key: keyof Donor, val: string | number) => setForm((prev) => ({ ...prev, [key]: val }));

  if (!canView) return <NoPermission resource="捐赠人管理" />;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-2.5 bg-[#18181B] text-white text-sm rounded-xl shadow-lg">{toast}</div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#18181B]">捐赠人管理</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">共 {donors.length} 位捐赠人/赞助商（会显示在首页轮播）</p>
        </div>
        {canWrite && (
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#18181B] text-white text-sm font-medium rounded-lg hover:bg-[#27272A] cursor-pointer whitespace-nowrap transition-colors">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>添加捐赠人
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
            <i className="ri-search-line text-[#9CA3AF] text-sm" />
          </div>
          <input type="text" placeholder="搜索名称..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none bg-white w-48" />
        </div>
        <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg bg-white focus:outline-none cursor-pointer">
          <option value="all">全部等级</option>
          {(Object.keys(TIER_LABEL) as DonorTier[]).map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F3F4F6] bg-[#FAFAFA]">
                {['标识', '名称', '类型', '等级', '年份', '简介', '操作'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[#9CA3AF]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex items-center justify-center gap-2 text-[#9CA3AF]">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#F59E0B]" />
                      <span>加载中...</span>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF] text-sm">暂无数据</td></tr>
              ) : filtered.map((d) => (
                <tr key={d.id} className="border-b border-[#F9FAFB] hover:bg-[#FAFAFA] transition-colors">
                  <td className="px-4 py-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: d.logo ? 'transparent' : d.color }}>
                      {d.logo ? (
                        <img src={d.logo} alt={d.name} className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-white text-[10px] font-bold">{d.abbr.slice(0, 4)}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#18181B] text-xs">{d.name}</div>
                    <div className="text-[#9CA3AF] text-[10px]">{d.nameEn}</div>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs">{d.type}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${TIER_COLOR[d.tier]}`}>{TIER_LABEL[d.tier]}</span>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280] text-xs">{d.year}</td>
                  <td className="px-4 py-3 text-[#9CA3AF] text-xs max-w-[200px] truncate">{d.descZh}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {canWrite && <button onClick={() => openEdit(d)} className="px-2 py-1 text-[10px] font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded cursor-pointer whitespace-nowrap transition-colors">编辑</button>}
                      {canDelete && <button onClick={() => setDeleteConfirm(d.id)} className="px-2 py-1 text-[10px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded cursor-pointer whitespace-nowrap transition-colors">删除</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6] shrink-0">
              <h3 className="font-bold text-[#18181B]">{editing ? '编辑捐赠人' : '添加捐赠人'}</h3>
              <button onClick={() => setShowModal(false)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-[#6B7280] cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="overflow-y-auto flex-1 px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                {([['name', '名称（中）'], ['nameEn', '名称（英）'], ['type', '类型（中）'], ['typeEn', '类型（英）'], ['abbr', '缩写标识（最多4字符）']] as [keyof Donor, string][]).map(([k, label]) => (
                  <div key={k} className={k === 'abbr' ? 'col-span-2' : ''}>
                    <label className="block text-xs font-medium text-[#374151] mb-1">{label}</label>
                    <input type="text" value={(form[k] as string) || ''} onChange={(e) => f(k, e.target.value)} required={k === 'name'}
                      className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B]" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">等级</label>
                  <select value={form.tier || 'gold'} onChange={(e) => f('tier', e.target.value)} className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none cursor-pointer">
                    {(Object.keys(TIER_LABEL) as DonorTier[]).map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">加入年份</label>
                  <input type="number" value={form.year || ''} onChange={(e) => f('year', Number(e.target.value))} min={2020} max={2030}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">标识颜色</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={(form.color as string) || '#F59E0B'} onChange={(e) => f('color', e.target.value)}
                      className="w-10 h-9 rounded border border-[#E5E7EB] cursor-pointer p-0.5" />
                    <input type="text" value={(form.color as string) || ''} onChange={(e) => f('color', e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none font-mono" />
                  </div>
                </div>
                <div className="col-span-2">
                  <ImageUploader
                    value={(form.logo as string) || ''}
                    onChange={(url) => f('logo', url)}
                    label="Logo 图片（可选，将替代字母标识显示）"
                    aspectClass="aspect-square"
                    placeholder="粘贴 Logo 图片 URL"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">简介（中）</label>
                  <textarea value={(form.descZh as string) || ''} onChange={(e) => f('descZh', e.target.value)} rows={2}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none resize-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1">简介（英）</label>
                  <textarea value={(form.descEn as string) || ''} onChange={(e) => f('descEn', e.target.value)} rows={2}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none resize-none" />
                </div>
              </div>
              <div className="flex gap-3 mt-5 pt-4 border-t border-[#F3F4F6]">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg cursor-pointer whitespace-nowrap transition-colors">取消</button>
                <button type="submit" className="flex-1 py-2.5 text-sm font-medium text-white bg-[#18181B] hover:bg-[#27272A] rounded-lg cursor-pointer whitespace-nowrap transition-colors">{editing ? '保存修改' : '添加'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-lg" />
            </div>
            <h3 className="font-bold text-[#18181B] text-center mb-2">确认删除</h3>
            <p className="text-sm text-[#6B7280] text-center mb-5">删除后无法恢复，确认删除吗？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 text-sm text-[#6B7280] bg-[#F3F4F6] rounded-lg cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
